'use client';

import { useState, useMemo } from 'react';
import * as echarts from 'echarts';
import { useTranslation } from '@/lib/i18n';
import { formatDateTime } from '@/lib/utils/date';
import { currencyFormatter } from '@/lib/utils';
import EChart from '@/components/common/EChart';
import useValueKline, {
  TIME_RANGE_INTERVAL_MAP,
  TimeRange,
  timeRanges,
} from '../hooks/use-value-kline';

export interface AssetChartCardProps {
  trackedWallets: string[];
  openInSidebar?: boolean;
}

const formatChartCurrency = (value: unknown) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '';

  return currencyFormatter(
    numericValue,
    Math.abs(numericValue) < 10000 ? { notation: 'standard' } : undefined,
  );
};

/**
 * 资产趋势图
 */
export function AssetChartCard({ trackedWallets, openInSidebar }: AssetChartCardProps) {
  const [selectedRange, setSelectedRange] = useState<TimeRange>('ALL');

  const { t } = useTranslation();
  const { points, isLoading } = useValueKline({
    wallets: trackedWallets,
    interval: TIME_RANGE_INTERVAL_MAP[selectedRange],
  });

  const handleRangeChange = (range: TimeRange) => {
    setSelectedRange(range);
  };

  // 下方曲线按当前时间窗口 user-pnl 的首尾值计算涨跌
  const firstHistoryValue = points[0]?.value;
  const lastHistoryValue = points[points.length - 1]?.value;
  const chartChangeValue =
    typeof firstHistoryValue === 'number' && typeof lastHistoryValue === 'number'
      ? lastHistoryValue - firstHistoryValue
      : 0;

  // 根据涨跌动态设置图表颜色
  const chartColor =
    chartChangeValue > 0 ? '#14C784' : chartChangeValue < 0 ? '#FF2D2D' : '#A772FF';

  const xAxisLabelFormat = selectedRange === '1D' ? 'HH:mm' : 'M.D';
  // 1D 之外的区间每个点跨度都以天计，时分没有意义，tooltip 只印日期
  const tooltipTimeFormat = selectedRange === '1D' ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD';
  const seriesData = useMemo(() => points.map((item) => [item.timestamp, item.value]), [points]);
  const yAxisExtent = useMemo(() => {
    const values = points
      .map((item) => item.value)
      .filter((value): value is number => Number.isFinite(value));

    if (!values.length) {
      return {};
    }

    const min = Math.min(...values);
    const max = Math.max(...values);

    if (min === max) {
      const padding = Math.max(Math.abs(min) * 0.01, 1);
      return {
        min: Math.floor(min - padding),
        max: Math.ceil(max + padding),
      };
    }

    return {
      min: Math.floor(min),
      max: Math.ceil(max),
    };
  }, [points]);

  const option = useMemo<echarts.EChartsOption>(() => {
    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(39, 39, 39, 0.9)',
        borderColor: '#4D4D4D',
        textStyle: { color: '#fff' },
        // 自定义 formatter 后 valueFormatter 不再生效，金额格式在下面自己套
        formatter: (params) => {
          const items = Array.isArray(params) ? params : [params];
          const timestamp = resolveTooltipTimestamp(items);
          const title = Number.isFinite(timestamp)
            ? formatDateTime(timestamp, tooltipTimeFormat)
            : '';
          const rows = items
            .map((item: any) => {
              const value = Array.isArray(item?.value) ? item.value[1] : item?.value;
              return `<div>${item.marker ?? ''}${formatChartCurrency(value)}</div>`;
            })
            .join('');

          return `<div>${title}${rows}</div>`;
        },
      },
      grid: {
        left: 0,
        right: 0,
        top: 10,
        bottom: 24,
        containLabel: false,
      },
      xAxis: {
        type: 'time',
        show: true,
        boundaryGap: [0, 0],
        axisLine: {
          show: true,
          lineStyle: {
            color: 'rgba(255,255,255,0.12)',
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: '#757575',
          fontSize: 11,
          margin: 8,
          hideOverlap: true,
          formatter: (value: number) => formatDateTime(value, xAxisLabelFormat),
        },
      },
      yAxis: {
        type: 'value',
        show: true,
        position: 'right',
        splitNumber: 3,
        min: yAxisExtent.min,
        max: yAxisExtent.max,
        minInterval: 1,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: '#757575',
          fontSize: 11,
          margin: 8,
          hideOverlap: true,
          formatter: formatChartCurrency,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(255,255,255,0.08)',
            type: 'dashed',
          },
        },
      },
      series: [
        {
          data: seriesData,
          type: 'line',
          smooth: true,
          symbol: 'none',
          lineStyle: {
            color: chartColor,
            width: 1.5,
          },
        },
      ],
      animation: true,
      animationDuration: 300,
    };
  }, [chartColor, seriesData, tooltipTimeFormat, xAxisLabelFormat, yAxisExtent]);

  return (
    <div className="relative rounded-lg p-4 bg-[#121212] border border-[#1F1F1F]">
      {isLoading && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-2xl z-10">
          <div className="w-8 h-8 border-4 border-[#5EEAD4] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* 时间范围选择器 */}
      <div className="flex gap-2.5">
        {timeRanges.map((range) => (
          <button
            key={range}
            className={`
                min-w-11.25 h-6 px-2.5 py-0.5 rounded-lg
                flex items-center justify-center
                text-[13px] font-medium uppercase
                transition-colors
                ${
                  selectedRange === range
                    ? 'bg-white/15 text-white'
                    : 'bg-transparent text-[#757575] hover:bg-white/5'
                }
              `}
            onClick={() => handleRangeChange(range)}
          >
            {t(range)}
          </button>
        ))}
      </div>
      {/* 趋势图表 */}
      <div className="relative mt-3">
        <EChart
          option={option}
          className="h-40 min-h-28"
          wrapperClassName="min-h-28"
          openInSidebar={openInSidebar}
        />
      </div>
    </div>
  );
}

/** axis 触发时优先用 axisValue，取不到再从数据点的 [timestamp, value] 里拿 */
function resolveTooltipTimestamp(items: any[]): number {
  const axisValue = items.find((item) => item?.axisValue !== undefined)?.axisValue;
  if (typeof axisValue === 'number') return axisValue;

  const point = items.find((item) => Array.isArray(item?.value))?.value?.[0];
  return typeof point === 'number' ? point : Number.NaN;
}
